import type { AutoTargetOnCombatEvent } from '@game/enums';
import type { PanelDimensions, PanelPosition, Position } from '@game/ui';

import type { InventoryState } from '../types/inventory.types';

type PanelInitialPosition = PanelPosition | 'bottomLeft' | 'bottomRight' | 'topLeft' | 'topRight' | 'center' | null;

export const defaultChatDimensions: PanelDimensions = { width: 650, height: 400 };
export const defaultChatPosition = { x: 0, y: 0 };

const defaultInventoryState = {
  filter: '',
  sort: {
    name: null,
    type: null,
    quality: null,
    quantity: null,
  },
};

const defaultKeyBindingsState = {
  combatSkill0: {
    text: 'Combat Skill 1',
    key: '1',
  },
  combatSkill1: {
    text: 'Combat Skill 2',
    key: '2',
  },
  combatSkill2: {
    text: 'Combat Skill 3',
    key: '3',
  },
  combatSkill3: {
    text: 'Combat Skill 4',
    key: '4',
  },
  combatSkill4: {
    text: 'Combat Skill 5',
    key: '5',
  },
  combatSkill5: {
    text: 'Combat Skill 6',
    key: '6',
  },
  combatSkill6: {
    text: 'Combat Skill 7',
    key: '7',
  },
  combatSkill7: {
    text: 'Combat Skill 8',
    key: '8',
  },
  combatSkill8: {
    text: 'Combat Skill 9',
    key: '9',
  },
  combatSkill9: {
    text: 'Combat Skill 10',
    key: '0',
  },
  combatTargetSelf: {
    text: 'Target Self',
    key: 'q',
  },
  combatTargetNextEnemy: {
    text: 'Target Next Enemy',
    key: 'e',
  },
  combatTargetNextAlly: {
    text: 'Target Next Ally',
    key: 'a',
  },
};

type Keybinding = keyof typeof defaultKeyBindingsState;

export type Keybindings = Record<
  Keybinding,
  {
    text: string;
    key: string;
  }
>;

export interface PersistentSettingsState {
  scrollToTopOnPageChange: boolean;
  smoothScrollToTopOnPageChange: boolean;
  highlightChatMentions: boolean;
  alertMissionReadyToComplete: boolean;
  autoRedirectToCombatInParty: boolean;
  autoPinNewMissions: boolean;
  autoTargetOnCombatStart: AutoTargetOnCombatEvent;
  autoTargetOnCombatKill: AutoTargetOnCombatEvent;
  keepSelectTargetAfterRound: boolean;
  keepSelectionOnReselectedEnemy: boolean;
  volume: number;
  sfxVolume: number;
  sfxUiEnabled: boolean;
  sfxCombatEnabled: boolean;
  sfxGameActionsEnabled: boolean;
  sfxCommerceEnabled: boolean;
  sfxChatEnabled: boolean;
  muteAudioOnTabNotInFocus: boolean;
  musicLoopDelay: number;
  panelOrder: string[];
  showChatPanel: boolean;
  chatDimensions: PanelDimensions;
  chatInitialPosition: PanelInitialPosition;
  chatPosition: Position;
  showModeratorPanel: boolean;
  moderatorDimensions: PanelDimensions;
  moderatorInitialPosition: PanelInitialPosition;
  moderatorPosition: Position;
  inventoryState: InventoryState;
  corporationInventoryState: InventoryState;
  desktopSidePanelsOrder: string[];
  keybindings: Keybindings;
  alwaysShowEquipmentComparison: boolean;
  tourStepIndex: number;
}

export const persistentSettingsDefault: PersistentSettingsState = {
  scrollToTopOnPageChange: true,
  smoothScrollToTopOnPageChange: true,
  highlightChatMentions: true,
  alertMissionReadyToComplete: true,
  autoRedirectToCombatInParty: true,
  autoPinNewMissions: true,
  autoTargetOnCombatStart: 0,
  autoTargetOnCombatKill: 0,
  keepSelectTargetAfterRound: true,
  keepSelectionOnReselectedEnemy: true,
  volume: 30,
  sfxVolume: 30,
  sfxUiEnabled: true,
  sfxCombatEnabled: true,
  sfxGameActionsEnabled: true,
  sfxCommerceEnabled: true,
  sfxChatEnabled: true,
  muteAudioOnTabNotInFocus: false,
  musicLoopDelay: 0, // milliseconds
  inventoryState: defaultInventoryState,
  corporationInventoryState: defaultInventoryState,
  chatDimensions: defaultChatDimensions,
  chatPosition: defaultChatPosition,
  chatInitialPosition: 'bottomRight',
  showChatPanel: false,
  moderatorPosition: { x: 100, y: 100 },
  moderatorDimensions: { width: 400, height: 500 },
  moderatorInitialPosition: 'center',
  showModeratorPanel: false,
  panelOrder: [],
  desktopSidePanelsOrder: ['mission-pins', 'equipment', 'inventory'],
  keybindings: defaultKeyBindingsState,
  alwaysShowEquipmentComparison: false,
  tourStepIndex: 0,
};
